/**
 * Admin Analytics Page
 *
 * Dashboard for viewing analytics, query logs, and monitoring user sessions.
 * The aggregate-analytics tabs are embedded Superset dashboards, sourced live
 * from GET /analytics/dashboards — the backend decides per-caller visibility
 * (OpenFGA), the frontend just renders whatever comes back. The remaining
 * tabs (raw query logs, sessions, feedback, etc.) stay app-native — Superset
 * is a no-free-text surface (superset_ro is denied the raw-text views at the
 * DB-grant level). Every tab registers in the fabric registry as an
 * agent-activatable `navigate` entry on the "admin" surface.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useGuideRegister, useSurface } from '@/hooks/useGuideRegister';
import { useAnalyticsDashboards } from '@/hooks/useAnalyticsDashboards';
import type { AnalyticsDashboard } from '@/api/endpoints/analytics';
import { SupersetDashboard } from '@/components/admin/SupersetDashboard';
import { SessionMonitor } from '@/components/admin/SessionMonitor';
import { ReportedErrors } from '@/components/admin/ReportedErrors';
import { DocumentApproval } from '@/components/admin/DocumentApproval';
import { OrgConsole } from '@/components/admin/org/OrgConsole';
import { PermissionsView } from '@/components/admin/org/PermissionsView';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// The app-native tabs — raw cross-user data, full-admin only. Order here
// drives the tab strip (after the dynamic Superset dashboard tabs); copy
// lives in i18n (admin.dashboard.tabs.* / admin.dashboard.tabHelp.*).
// The queries/clusters/feedback tabs are gone: the cross-user query-log,
// cluster and feedback reads died with the conversation domain's move to
// tricorder-session (no upstream cross-user route exists).
const STATIC_TABS = [
  'sessions',
  'reported-errors',
  'approvals',
  'organisation',
  'permissions',
] as const;
type StaticTab = (typeof STATIC_TABS)[number];

/**
 * What governs each app-native tab. This is the ONE place the tab strip and the
 * server's gate are tied together, and it exists because they used to disagree:
 * the strip was drawn from `isPlatformAdmin` alone, so an admin could grant
 * `endpoint:admin.queries viewer`, the route would honour it, and the grantee's
 * dashboard would still be empty — per-endpoint delegation was enforced but
 * invisible, which is what made the Permissions console look like it covered
 * "only a subset" of the dashboard.
 *
 *   `endpoint`  the org.endpoint_kinds instance the route checks. Visible to a
 *               full admin OR to whoever holds the grant — mirroring
 *               AdminEndpointACLMiddleware exactly, which passes on the
 *               platform-admin short-circuit FIRST and only then checks
 *               `endpoint:<id> viewer`.
 *
 *               The full-admin branch is not redundant. It was omitted at first
 *               on the assumption that `admin_endpoints` would already contain
 *               every instance for an admin via the model's owner-from-system
 *               backstop — and on staging it did not: that derivation resolves
 *               through Check (so every route returned 200) but the reverse
 *               index came back with a subset, so an admin lost the Queries and
 *               Clusters tabs while still being able to open both routes. A menu
 *               derived from a reverse-index listing must never be the only path
 *               to an area the CHECK would allow; where the two disagree, the
 *               check wins and the menu follows it.
 *   `approver`  governed by system:platform#approver, not an endpoint id:
 *               approval has its own delegable relation, and minting a parallel
 *               endpoint for it would be a second mechanism for one decision.
 *   `fullAdmin` deliberately NOT delegable. Permissions is full-admin because
 *               delegating the power to delegate is privilege escalation;
 *               Organisation is governed per-unit by the node graph itself; the
 *               Sessions monitor reads the caller's OWN sessions, so there is no
 *               cross-user access to hand out.
 */
const TAB_GATES: Record<StaticTab, { endpoint?: string; approver?: true; fullAdmin?: true }> = {
  'reported-errors': { endpoint: 'admin.client_errors' },
  approvals: { approver: true },
  sessions: { fullAdmin: true },
  organisation: { fullAdmin: true },
  permissions: { fullAdmin: true },
};

/**
 * The panel each app-native tab mounts. Co-located with TAB_GATES so a tab
 * cannot gain a gate without a panel, or a panel without a gate — the two lists
 * are keyed by the same StaticTab union, so TypeScript rejects a partial map.
 */
const STATIC_TAB_PANELS: Record<
  StaticTab,
  (props: { deepLinkSessionId: string | null }) => React.ReactNode
> = {
  sessions: ({ deepLinkSessionId }) => <SessionMonitor initialSessionId={deepLinkSessionId} />,
  'reported-errors': () => <ReportedErrors />,
  approvals: () => <DocumentApproval />,
  organisation: () => <OrgConsole />,
  permissions: () => <PermissionsView />,
};

// Stable empty-array reference so `dashboards ?? EMPTY_DASHBOARDS` doesn't
// create a new array identity every render (which would defeat the
// useMemo below and re-run the tab-clamp effect on every tick).
const EMPTY_DASHBOARDS: AnalyticsDashboard[] = [];
// Same stable-identity reason, for the granted-id lists off the auth store: a
// fresh `[]` per render would re-run the visibleTabs memo on every tick.
const EMPTY_IDS: string[] = [];

// Fabric (Agentic UI Fabric): each tab registers as an agent-activatable
// `navigate` entry for its mounted lifetime, so the ui_context manifest
// advertises exactly the tabs THIS caller can see and a guide directive can
// switch the surface to the dashboard the agent is talking about. One
// wrapper instead of a hook call per tab literal — the dashboard tabs are a
// dynamic, ACL-filtered list.
function AdminTabTrigger({
  value,
  labelKey,
  onActivate,
  children,
}: {
  value: string;
  labelKey: string;
  onActivate: (tab: string) => void;
  children: React.ReactNode;
}) {
  const activate = useCallback(() => onActivate(value), [onActivate, value]);
  const guideProps = useGuideRegister(
    {
      guideId: `admin.tab.${value}`,
      surfaceId: 'admin',
      kind: 'navigate',
      labelKey,
    },
    activate,
  );
  return (
    <TabsTrigger value={value} {...guideProps}>
      {children}
    </TabsTrigger>
  );
}

// Trial-countdown unit. A calendar constant (24×60×60×1000), not a tunable —
// the trial LENGTH lives on the backend; the UI only renders the days left.
const MS_PER_DAY = 86_400_000;
// How often the "N days left" hint recomputes `now` so a long-open dashboard
// doesn't freeze the count at mount time. A UI RENDER CADENCE (like an animation
// duration), NOT a business/infra tunable — deliberately a named source const and
// exempt from the config-driven rule; the trial LENGTH (the real tunable) lives in
// backend config (org.new_user_admin_view_trial_days). Cosmetic; gates nothing.
const TRIAL_COUNTDOWN_REFRESH_MS = 60_000;

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const mainAuth = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  // Two tiers, both degrade closed when the user (or a flag) is unknown:
  //  - isFullAdmin (read + write): the raw cross-user tabs + permission mgmt.
  //  - canViewAdmin (read-only): the section + the Superset dashboard tabs.
  //    True for full admins, delegated read-only admins, AND new users in
  //    their 30-day trial.
  const isFullAdmin = useAuthStore((s) => s.user?.isPlatformAdmin ?? false);
  const adminView = useAuthStore((s) => s.user?.adminView ?? false);
  const trialExpiresAt = useAuthStore((s) => s.user?.adminViewTrialExpiresAt ?? null);
  // The delegated tiers: the admin areas granted to this caller, and the
  // approver authority. Both degrade closed when unknown.
  const adminEndpoints = useAuthStore((s) => s.user?.adminEndpoints) ?? EMPTY_IDS;
  const canApprove = useAuthStore((s) => s.user?.canApprove ?? false);
  // The Admin SECTION opens for the read tier OR for anyone holding at least one
  // delegated area OR approval authority. A per-endpoint grantee and an approver
  // both have no admin_viewer tier, so gating the section on the read tier alone
  // would bounce them home and make the grant they were given unusable — the
  // enforced-but-unreachable shape this change removes.
  const canViewAdmin = isFullAdmin || adminView || canApprove || adminEndpoints.length > 0;
  // Deep links (?tab=sessions&session=<id>) are read SYNCHRONOUSLY in the
  // state initializers, not in a mount effect: the mount effect raced the
  // tab-clamp effect below — both ran in the same commit, the clamp closed
  // over the pre-deep-link activeTab ('') and clobbered the requested tab
  // with visibleTabs[0] whenever the admin flags were already resolved.
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    if (tab) return tab;
    return searchParams.get('session') ? 'sessions' : '';
  });
  const [deepLinkSessionId] = useState<string | null>(() => searchParams.get('session'));

  // Days left on the 30-day new-user admin-view trial, when present. Only a
  // hint; rendered solely for a view-tier user whose trial is still running.
  // `now` is refreshed every 60 s so the count stays current on a long-open
  // dashboard rather than freezing at mount time.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TRIAL_COUNTDOWN_REFRESH_MS);
    return () => clearInterval(id);
  }, []);
  const trialDaysLeft = useMemo(() => {
    if (!trialExpiresAt) return null;
    const expiry = new Date(trialExpiresAt).getTime();
    if (Number.isNaN(expiry)) return null;
    const days = Math.ceil((expiry - now) / MS_PER_DAY);
    return days > 0 ? days : null;
  }, [trialExpiresAt, now]);

  // The Superset dashboard tabs (Overview, Latency, Usage). The backend
  // filters to what THIS caller may see and never calls Superset at all when
  // that's empty — an empty result is a normal, ungated state, not an error.
  const {
    data: dashboards,
    isLoading: dashboardsLoading,
    error: dashboardsError,
    refetch: refetchDashboards,
    isRefetching: dashboardsRefetching,
  } = useAnalyticsDashboards();
  const dashboardTabs = dashboards ?? EMPTY_DASHBOARDS;

  // One rule per tab, from TAB_GATES — the strip now shows exactly what this
  // caller's grants open, in the declared order.
  const visibleStaticTabs = useMemo(
    () =>
      STATIC_TABS.filter((tab) => {
        const gate = TAB_GATES[tab];
        if (gate.fullAdmin) return isFullAdmin;
        // A full admin passes every delegable gate at the server (the
        // middleware's short-circuit), so the menu says so too.
        if (isFullAdmin) return true;
        if (gate.approver) return canApprove;
        return gate.endpoint ? adminEndpoints.includes(gate.endpoint) : false;
      }),
    [isFullAdmin, canApprove, adminEndpoints],
  );
  const visibleTabs = useMemo(
    () => [...dashboardTabs.map((d) => d.slug), ...visibleStaticTabs],
    [dashboardTabs, visibleStaticTabs],
  );

  // Fabric: this page is the "admin" surface (mirrors Home's 'home' /
  // Session's 'transcript') so the ui_context manifest attributes the
  // registered tab entries to the right surface.
  useSurface('admin');

  // Clean the deep-link params off the URL after the initializers above read them.
  useEffect(() => {
    if (searchParams.get('tab') || searchParams.get('session')) {
      setSearchParams({}, { replace: true });
    }
    // Mount-only: this strips the deep link the initializers above already read.
    // Depending on searchParams would make the wipe fire again on any later
    // param the page sets for itself, clearing it right back out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clamp the active tab to one the caller can currently see: covers a deep
  // link to a tab this tier doesn't have, AND the initial load before the
  // dashboard registry (async) or the admin flags have resolved.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [visibleTabs, activeTab]);

  // Admin access requires login — ACL check happens server-side
  if (!mainAuth) {
    void navigate('/login');
    return null;
  }

  // The Admin section is for the view tier and up (full admins, delegated
  // read-only admins, new users in their 30-day trial). A logged-in user who is
  // neither has no surface here — bounce home rather than render an empty shell
  // whose every endpoint would 403. Degrades closed: an unknown flag reads false.
  if (!canViewAdmin) {
    void navigate('/home');
    return null;
  }

  const isStaticTab = (STATIC_TABS as readonly string[]).includes(activeTab);

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-neutral-200">
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/home')}
              aria-label={t('common.backHome', 'Back to Home')}
              className="w-9 h-9 -ml-2 flex items-center justify-center text-primary"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </button>
            <h1 className="text-base font-semibold text-neutral-900">
              {t('admin.dashboard.title', 'Admin Dashboard')}
            </h1>
            {/* 30-day new-user admin-view trial hint. Shown only to a view-tier
                user inside the trial (a full admin never has an expiry), and
                only when the design fits — a quiet neutral pill, never a nag. */}
            {!isFullAdmin && trialDaysLeft !== null && (
              <span
                className="hidden sm:inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-0.5 text-[11px] font-medium text-neutral-500"
                title={t('admin.dashboard.previewTooltip', { count: trialDaysLeft })}
              >
                {t('admin.dashboard.previewBadge', { count: trialDaysLeft })}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-neutral-500 hover:text-neutral-700"
          >
            {t('auth.logout', 'Log Out')}
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 max-w-6xl mx-auto">
        {dashboardsError && (
          <div className="mb-4 p-4 bg-red-50 rounded-xl text-sm">
            <div className="flex items-start justify-between gap-4">
              <p className="text-red-600">
                {t('admin.analytics.dash.loadError', 'Failed to load dashboard — please retry.')}
              </p>
              <Button
                onClick={() => void refetchDashboards()}
                disabled={dashboardsRefetching}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 shrink-0"
              >
                <RefreshCw className={`w-4 h-4 ${dashboardsRefetching ? 'animate-spin' : ''}`} />
                {dashboardsRefetching ? t('actions.retrying') : t('actions.retry')}
              </Button>
            </div>
          </div>
        )}

        {dashboardsLoading && visibleTabs.length === 0 ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-9 w-24 rounded-lg" />
              ))}
            </div>
            <Skeleton className="h-96 w-full rounded-xl" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex w-full overflow-x-auto mb-4 [&>button]:flex-none [&>button]:px-2.5 [&>button]:transition-none">
              {dashboardTabs.map((d) => (
                <AdminTabTrigger
                  key={d.slug}
                  value={d.slug}
                  labelKey={d.title_key}
                  onActivate={setActiveTab}
                >
                  {t(d.title_key)}
                </AdminTabTrigger>
              ))}
              {visibleStaticTabs.map((value) => (
                <AdminTabTrigger
                  key={value}
                  value={value}
                  labelKey={`admin.dashboard.tabs.${value}`}
                  onActivate={setActiveTab}
                >
                  <span title={t(`admin.dashboard.tabHelp.${value}`)}>
                    {t(`admin.dashboard.tabs.${value}`)}
                  </span>
                </AdminTabTrigger>
              ))}
            </TabsList>

            {/* Tab description — app-native tabs only. The embedded Superset
                dashboards are self-descriptive; a redundant one-liner above
                them would just be paternalistic copy. */}
            {isStaticTab && (
              <p className="text-xs text-neutral-400 mb-4 -mt-2">
                {t(`admin.dashboard.tabHelp.${activeTab as StaticTab}`)}
              </p>
            )}

            {dashboardTabs.map((d) => (
              <TabsContent key={d.slug} value={d.slug}>
                <SupersetDashboard
                  slug={d.slug}
                  embeddedUuid={d.embedded_uuid}
                  embedHost={d.embed_host}
                />
              </TabsContent>
            ))}

            {/* Panels are mounted per VISIBLE tab, so a caller only ever mounts
                the areas their grants open and the raw cross-user endpoints
                behind the others are never called. Keyed off the same
                visibleStaticTabs the strip renders, so a tab can never exist
                without its panel (or a panel without its tab). */}
            {visibleStaticTabs.map((value) => (
              <TabsContent key={value} value={value}>
                {STATIC_TAB_PANELS[value]({ deepLinkSessionId })}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </main>
    </div>
  );
}
